import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import mergeRefs from 'merge-refs';


const SPINNER_BUTTON_LONG_PRESS_INTERVAL = 50;
const SPINNER_BUTTON_LONG_PRESS_INITIAL_DELAY = 300;

interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    min?: number;
    max?: number;
    step?: number;
    width?: number | string;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>((props, propsRef) => {
    const { className: propsClassName, width, ...rest } = props;

    const inputRef = useRef<HTMLInputElement>(null);

    const stepOn = useCallback((value: number, increase: boolean) => {
        const step = rest.step ?? 1;
        const decimalPlaces = step.toString().split(".")[1]?.length ?? 0;
        const multiplier = 10 ** decimalPlaces;

        const nextValue = value + step * (increase ? 1 : -1);
        return Math.round(nextValue * multiplier) / multiplier;
    }, [rest.step]);

    const clamp = useCallback((value: number) => {
        const min = rest.min;
        const max = rest.max;
        if (min !== undefined && value < min) {
            return min;
        }
        if (max !== undefined && value > max) {
            return max;
        }
        return value;
    }, [rest.min, rest.max]);

    const triggerChangeEvent = useCallback((value: string) => {
        if (!inputRef.current) {
            return;
        }
        const inputEvent = new Event('change', { bubbles: true });
        inputRef.current.value = value;
        inputRef.current.dispatchEvent(inputEvent);
        if (rest.onChange) {
            rest.onChange({ ...rest, target: { value } } as any);
        }
    }, [rest]);

    const onPressSpinnerButton = useCallback((increase: boolean) => {
        if (!inputRef.current) {
            return;
        }
        const currentValue = Number(rest.value ?? inputRef.current.value);
        const steppedValue = stepOn(currentValue, increase);
        const newValue = clamp(steppedValue);
        triggerChangeEvent(String(newValue));
    }, [rest.value, stepOn, clamp, triggerChangeEvent]);

    const onBlur = useCallback(() => {
        if (!inputRef.current) {
            return;
        }
        const currentValue = Number(rest.value ?? inputRef.current.value);
        const newValue = clamp(currentValue);
        triggerChangeEvent(String(newValue));
    }, [rest, clamp, triggerChangeEvent]);

    return (
        <div className="relative flex-none" style={{ width }}>
            <input type="number"
                   ref={mergeRefs(inputRef, propsRef)}
                   className={classNames("input-number", propsClassName)}
                   {...rest}
                   onBlur={e => {
                       onBlur();
                       rest.onChange?.(e);
                   }}
            />
            <SpinnerButtonGroup className="absolute right-2 top-1/2 -translate-y-1/2"
                                onPress={onPressSpinnerButton}
            />
        </div>
    );
});

interface SpinnerButtonGroupProps {
    className?: string;
    onPress: (increase: boolean) => void;
}

const SpinnerButtonGroup: React.FC<SpinnerButtonGroupProps> = (props) => {
    const { className: propsClassName, onPress: updateValue } = props;

    const [isLongPressing, setIsLongPressing] = useState(false);
    const [isPressing, setIsPressing] = useState(false);
    const [buttonPressed, setButtonPressed] = useState<"increase" | "decrease">();

    const onClickMouseDown = (increase: boolean) => {
        updateValue(increase);

        setIsPressing(true);
        setButtonPressed(increase ? "increase" : "decrease");
    };

    const onClickMouseUp = () => {
        setIsPressing(false);
        setButtonPressed(undefined);
    };

    useEffect(() => {
        if (isPressing) {
            const timer = setInterval(() => {
                setIsLongPressing(true);
            }, SPINNER_BUTTON_LONG_PRESS_INITIAL_DELAY);

            return () => clearInterval(timer);
        } else {
            setIsLongPressing(false);
        }
    }, [isPressing, updateValue]);

    useEffect(() => {
        if (isLongPressing) {
            const timer = setInterval(() => {
                updateValue(buttonPressed === "increase");
            }, SPINNER_BUTTON_LONG_PRESS_INTERVAL);
            
            return () => clearInterval(timer);
        }
    }, [isLongPressing, buttonPressed, updateValue]);

    return (
        <div aria-hidden={true}
             className={classNames("flex flex-col text-black-300", propsClassName)}
        >
            <button tabIndex={-1}
                    className="hover:bg-black-100 rounded-[4px]"
                    onMouseDown={() => onClickMouseDown(true)}
                    onMouseUp={onClickMouseUp}
                    onMouseLeave={onClickMouseUp}
            >
                <svg className="stroke-current" width="16" height="12" viewBox="0 0 16 8" fill="none">
                    <path d="M5 6L8.5 2L12 6" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            <button tabIndex={-1}
                    className="hover:bg-black-100 rounded-[4px]"
                    onMouseDown={() => onClickMouseDown(false)}
                    onMouseUp={onClickMouseUp}
                    onMouseLeave={onClickMouseUp}
            >
                <svg className="stroke-current" width="16" height="12" viewBox="0 0 16 8" fill="none">
                    <path d="M12 2L8.5 6L5 2" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
};

export default NumberInput;
